import { PRAYERS } from '@/domain/prayer';
import {
  elapsedSinceFajr,
  placeholderTimes,
  sinceFajr,
  type PrayerTimeSet,
} from '@bonyaan/prayer-times';

/**
 * The mark: an astrolabe, its ring divided into five.
 *
 * The muwaqqit's own instrument — the tool prayer times were worked out with
 * for centuries — with as many divisions as the thing this app counts.
 *
 * It is not a decoration of a clock, it IS one. The five marks sit where the
 * five actually fall in today's prayer-day — the times the app has actually
 * calculated for this location and date, not a drawing of five even divisions
 * — so they crowd through the afternoon and leave the long empty arc of the
 * night, and they move through the year as the times do. The alidade points at
 * this minute. Everything is measured from Fajr at the top, which is the same
 * day the record uses.
 *
 * It also says where in the day you are without being read: a prayer whose
 * time has come is cut at full strength, one still to come is faint. So the
 * ring fills in over the day, five times, and the mark in the bar is worth
 * glancing at rather than being a logo that happens to be a circle. `now` is
 * the app's own ticking clock where one is passed, so it moves at each adhān
 * on a screen left open.
 *
 * Drawn rather than loaded: it reads the theme's own tokens, so the same
 * markup is a gold disc in the kids skin and an outlined one in the plain, and
 * it stays sharp at any size where a raster would not.
 */
const SAMPLE = placeholderTimes.forDate('1970-01-01');

/** Angle in degrees from the top, clockwise, to a point on the ring. */
function point(deg: number, radius: number, cx = 24, cy = 25) {
  const a = ((deg - 90) * Math.PI) / 180;
  return [cx + radius * Math.cos(a), cy + radius * Math.sin(a)] as const;
}

function spoke(deg: number, from: number, to: number) {
  const [x1, y1] = point(deg, from);
  const [x2, y2] = point(deg, to);
  return `M${x1.toFixed(1)} ${y1.toFixed(1)} ${x2.toFixed(1)} ${y2.toFixed(1)}`;
}

export function Logo({
  size = 32,
  title,
  times = SAMPLE,
  now = new Date(),
  hanging = false,
}: {
  size?: number;
  title?: string;
  times?: PrayerTimeSet;
  now?: Date;
  /**
   * Draw the shackle it hangs by. An astrolabe is used suspended from its
   * ring, sighted along the alidade — which is also what makes the entry
   * screen's gentle swing read as a swing rather than a wobble.
   */
  hanging?: boolean;
}) {
  const elapsed = elapsedSinceFajr(times.fajr, now);
  // A prayer whose time has come, against one still ahead. Fajr is always in:
  // the day is measured from it, so nothing sits before it.
  const marks = PRAYERS.map((p) => {
    const at = sinceFajr(times[p], times.fajr);
    return { deg: (at / 1440) * 360, due: at <= elapsed };
  });
  const hand = (elapsed / 1440) * 360;
  const [hx, hy] = point(hand, 13.6);
  const [tx, ty] = point(hand + 180, 5);

  return (
    <svg
      width={size}
      height={hanging ? (size * 62) / 48 : size}
      viewBox={hanging ? '0 -14 48 62' : '0 0 48 48'}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
      style={{ filter: 'var(--ink-drop)' }}
    >
      {hanging ? (
        /* The shackle hangs ABOVE the disc, on the sky rather than on the gold,
           so it takes the accent meant for the sky. In the outline colour it
           was dark on dark and did not appear at all. */
        <g stroke="var(--primary)" strokeWidth="2.2" strokeLinecap="round">
          {/* The kursī, the throne a real astrolabe hangs by. A bare stem ran
              straight into the Fajr mark at twelve o'clock and swallowed it;
              a shape sits outside the disc instead of along it. */}
          <path d="M18.6 8.4Q19.6 2.2 24 2.2t5.4 6.2Z" fill="var(--primary)" strokeWidth="1.6" />
          <path d="M24 2.2 24 -3" fill="none" />
          <circle cx="24" cy="-7.6" r="4.6" fill="none" />
        </g>
      ) : null}
      <circle
        cx="24"
        cy="25"
        r="17"
        fill="var(--hero-bg)"
        stroke="var(--outline)"
        strokeWidth="2"
      />
      {/* The five, in the ink of the ring, the ones still to come left faint.
          The alidade is the accent, because it is the part that moves — and so
          the two never read as one shape. */}
      <g stroke="var(--outline)" strokeWidth="2.4" strokeLinecap="round" fill="none">
        {marks.map((mark, i) => (
          <path
            key={PRAYERS[i]}
            d={spoke(mark.deg, 16.2, 11.4)}
            opacity={mark.due ? undefined : 0.32}
          />
        ))}
      </g>
      <circle
        cx="24"
        cy="25"
        r="9.6"
        fill="none"
        stroke="var(--outline)"
        strokeWidth="1.4"
        opacity=".55"
      />
      <path
        d={`M${tx.toFixed(1)} ${ty.toFixed(1)} ${hx.toFixed(1)} ${hy.toFixed(1)}`}
        stroke="var(--accent-ink)"
        strokeWidth="2.6"
        strokeLinecap="round"
        fill="none"
      />
      <circle
        cx="24"
        cy="25"
        r="2.7"
        fill="var(--accent-ink)"
        stroke="var(--outline)"
        strokeWidth="1.6"
      />
    </svg>
  );
}
